import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import DataTable from "../components/admin/DataTable";
import api from "../api";

const DATASET_CONFIG = {
  users: { endpoint: "/firestore/users", responseKey: "users", title: "Users" },
  subscriptions: { endpoint: "/firestore/subscriptions", responseKey: "subscriptions", title: "Subscriptions" },
  "subscribers count": {
    endpoint: "/firestore/subscriptions",
    responseKey: "subscriptions",
    title: "Subscribers Count",
    mode: "count"
  }
};

const toCellText = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => toCellText(item)).join(", ");
  return JSON.stringify(value);
};

const collectKeys = (rows) => {
  const keys = new Set();
  rows.slice(0, 50).forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (key !== "id") keys.add(key);
    });
  });
  return Array.from(keys);
};

const ModulePlaceholderPage = ({ module, item }) => {
  const key = String(item?.label || "").trim().toLowerCase();
  const config = DATASET_CONFIG[key];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!config) {
      setRows([]);
      setLoading(false);
      setError("");
      return () => {
        active = false;
      };
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get(config.endpoint);
        if (!active) return;
        setRows(Array.isArray(response.data?.[config.responseKey]) ? response.data[config.responseKey] : []);
      } catch (err) {
        if (!active) return;
        setRows([]);
        setError(err.response?.data?.message || `Could not load ${config.title.toLowerCase()} data`);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [config]);

  const columns = useMemo(() => {
    if (!config) return [];
    const dataKeys = collectKeys(rows);
    const dynamicColumns = dataKeys.map((dataKey) => ({
      key: dataKey,
      label: dataKey.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      accessor: (row) => toCellText(row?.[dataKey])
    }));

    return [
      {
        key: "index",
        label: "#",
        accessor: () => "",
        searchable: false,
        render: (_row, index) => index
      },
      { key: "id", label: "Id", accessor: (row) => toCellText(row?.id) },
      ...dynamicColumns
    ];
  }, [config, rows]);

  if (!config) {
    return (
      <AdminLayout module={module} title={item.label}>
        <section className="placeholder-card">
          <h2>{item.label}</h2>
          <p>This page is UI-only right now; its backend endpoint is not implemented yet.</p>
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout module={module} title={item.label}>
      {error && <p className="error-banner">{error}</p>}
      {loading ? (
        <p className="loading-text">Loading {config.title.toLowerCase()}...</p>
      ) : config.mode === "count" ? (
        <section className="placeholder-card">
          <h2>{config.title}</h2>
          <p>Total subscribers: <strong>{rows.length}</strong></p>
        </section>
      ) : (
        <DataTable columns={columns} rows={rows} />
      )}
    </AdminLayout>
  );
};

export default ModulePlaceholderPage;
